import { Component, inject, OnInit, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ThemeService } from '@awo-care/utils';
import { environment } from '@awo-care/shared-auth';
import { Router, RouterOutlet, Scroll } from '@angular/router';
import { LanguageService } from '@awo-care/shared-core';
import {
  CookieConsentComponentWrapper,
  skipToMainContent,
} from '@awo-care/shared-ui';
import { NavbarComponent } from './core/components/navbar/navbar.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { ViewportScroller } from '@angular/common';
import { filter } from 'rxjs';
import { versionWebsite } from 'package.json';
@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    CookieConsentComponentWrapper,
    NavbarComponent,
    FooterComponent,
  ],
  // <main> und Footer liegen bewusst in der Shell und nicht in den Route-
  // Komponenten: der Skip-Link aus index.html braucht auf JEDER Route ein
  // #main-content, und die Barrierefreiheitserklaerung muss laut BITV 2.0 von
  // jeder Seite aus erreichbar sein (Footer-Link). Der Footer steht ausserhalb
  // von <main>, sonst waere das contentinfo-Landmark verschachtelt.
  template: `
    <a
      href="#main-content"
      class="skip-link"
      (click)="skipToMainContent($event)"
      i18n="@@accessibility.skipLink"
      >Zum Hauptinhalt springen</a
    >
    <app-navbar />
    <main id="main-content" tabindex="-1">
      <router-outlet />
    </main>
    <app-footer />
    <!--
      Der Consent-Dialog steht bewusst NACH dem Inhalt: er ist position:fixed,
      optisch aendert die Reihenfolge nichts. Stand er davor, war er der erste
      zusammenhaengende Fliesstextblock des Dokuments — LLM-Scraper
      (ElevenLabs, Firecrawl, Jina) lasen dann ihn statt der Seite ein und die
      Wissensdatenbank bekam nur den Cookie-Text. Siehe #1027.
    -->
    <lib-care-cookie-consent-wrapper />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  protected readonly skipToMainContent = skipToMainContent;
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  languageService = inject(LanguageService);

  constructor() {
    if (!this.isBrowser) return;

    console.log(
      `%c AWO Care Website %cv${versionWebsite}`,
      'background: #1976d2; color: white; padding: 2px 6px; font-weight: bold;',
      'background: #424242; color: white; padding: 2px 6px;'
    );
    this.themeService.initializeTheme('brand-theme');
    if (!environment.production) {
      const splashScreen = document.getElementById('splash-screen');
      splashScreen?.remove();
    }

    // Handle fragment scrolling with navbar offset
    this.router.events
      .pipe(filter((e): e is Scroll => e instanceof Scroll))
      .subscribe((e) => {
        const anchor = e.anchor;
        if (anchor) {
          // Wait for DOM to be ready
          setTimeout(() => {
            const element = document.getElementById(anchor);
            if (element) {
              const navbarHeight = 72;
              const elementPosition =
                element.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - navbarHeight;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }
          }, 100);
        } else if (e.position) {
          this.viewportScroller.scrollToPosition(e.position);
        } else {
          this.viewportScroller.scrollToPosition([0, 0]);
        }
      });
  }

  ngOnInit() {
    if (!this.isBrowser) return;

    // Ohne kuenstliche Verzoegerung ausblenden: der Inhalt ist prerendert und
    // steht bereits da. Jede Wartezeit hier verdeckt ihn nur — auch fuer
    // LLM-Scraper, die mit kurzem Timeout rendern (#1025).
    const splashScreen = document.getElementById('splash-screen');
    if (splashScreen) {
      splashScreen.classList.add('hide');
      setTimeout(() => splashScreen.remove(), 500);
    }
  }
}
